import login from 'api/auth/login'
import { useDialog } from 'components/DialogV3'
import DialogAcceptPolicy from 'components/DialogV3/DialogAcceptPolicy'
import { PATH_ROOT } from 'constants/api'
import { LOGIN_STEP } from 'pages/authentication/login/constant'
import { setLoginStep } from 'pages/authentication/login/loginSlice'
import { updateSSOFlow } from 'pages/authentication/AuthFlowContext'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import { selectCurrentUserPath } from 'store/selectors'
import { establishSession } from 'store/sessionSlice'
import { updatePathApp } from 'store/user/userSlice'
import { validateDomain, toastError, toastSuccess, toastWarning } from 'utils/helpers'

const useLogin = () => {
    const [params] = useSearchParams();
    const { t } = useTranslation()
    const navigate = useNavigate();
    const { openDialog } = useDialog()
    const dispatch = useDispatch()
    const [, setGlobalVariable] = useOutletContext();
    const pathApp = useSelector(selectCurrentUserPath);
    const domain = validateDomain(params.get('domain'))

    const handleLoginSuccess = (data) => {
        dispatch(setLoginStep(LOGIN_STEP.SYSTEM))
        toastSuccess(t('toast.loginSuccess'));

        const accessToken = data?.accessToken || data?.token
        const refreshToken = data.refreshToken

        dispatch(establishSession({ accessToken, refreshToken }));

        localStorage.setItem("checkEventPopupByUser", 1);
        if (domain) {
            window.location.href = domain + '?token=' + accessToken;
        } else {
            setGlobalVariable(prev => ({ ...prev, isRedirectFromLogin: true }))
            setTimeout(() => {
                navigate(pathApp || PATH_ROOT)
                dispatch(updatePathApp(''))
            }, 1000);
        }
    }

    const handleRejectSharingInfo = () => toastError(t('toast.error.title.loginError'), t('toast.error.content.sharingInfo'), { autoClose: 5000 })

    const handleLoginWithPolicy = async ({ data, captcha, setCaptcha, policies }) => {
        try {
            const response = await dispatch(
                login({ ...data, pi_share_status: 1, pi_share_policies: policies }),
            ).unwrap();

            handleLoginResponse({ data, captcha, setCaptcha, response })
        } catch (error) {
            
        }
    }

    const handleLoginResponse = ({ data, captcha, setCaptcha, response }) => {
        if (!response) return

        if (response.status === 200) {
            localStorage.setItem("checkEventPopupByUser", 1);
            switch (response.message) {
                case 'ER_MUST_FIRST_CHANGE_PASSWORD': {
                    toastSuccess(t('toast.firstChangePwd'));
                    navigate(
                        `${PATH_ROOT}/auth/change-password?username=${data?.email || data?.username}` +
                        (domain ? `&domain=${domain}` : ''),
                    );
                    break;
                }
                case 'PASSWORD_EXPIRED': {
                    toastError(t('toast.expiredPwd'));
                    navigate(
                        `${PATH_ROOT}/auth/change-password?username=${data?.email || data?.username}` +
                        (domain ? `&domain=${domain}` : ''),
                    );
                    break;
                }
                case 'WARNING_PASSWORD_EXPIRED': {
                    toastWarning(t('toast.expiredPasswordWarning', { day: response.timeleft }));
                    break;
                }
                case 'PI_SHARE_NOT_ALLOW': {
                    openDialog(DialogAcceptPolicy, {
                        onAction: (policies) => handleLoginWithPolicy({ data, captcha, setCaptcha, policies}),
                        onReject: handleRejectSharingInfo
                    })
                    break;
                }
                case 'AUTH_2FA_ENABLED': {
                    updateSSOFlow({ tempToken: response.temp_token })
                    dispatch(setLoginStep(LOGIN_STEP.ENTER_2FA))
                    break;
                }
                default:
                    handleLoginSuccess(response?.detail)
                    break;

            }
        } else {
            if (response.message === 'ER_ACCOUNT_LOOKED') {
                toastError(t('toast.accountLocked'));
            } else if (response.detail_error.includes('Captcha is invalid')) {
                if (captcha) {
                    toastError(t('toast.captchaIsWrong'));
                } else toastWarning(t('toast.pleaseEnterCaptcha'));
            } else {
                toastError(t('toast.accountIsWrong'));
            }
            if (response.captcha != null) {
                setCaptcha(response.captcha);
            }
        }
    }

    return {
        handleLoginResponse,
        handleLoginSuccess,
        handleRejectSharingInfo
    }
}

export default useLogin
